import "@ids/style-button";
import "@ids/style-css-helpers";
import "@ids/style-dropdown-list";
import "@ids/style-dropdown-select";
import "@ids/style-error-message";
import "@ids/style-input-field";
import "@ids/style-label";
import "@ids/style-loader";
import "@ids/style-modal-v19";
import "@ids/style-scaffolding";
import type { ComponentType } from "react";
import { createElement } from "react";
import { ProductType } from "@/api_client";
import { getWebComponentLanguage } from "./hooks/locationLanguage";
import { getWebComponentAppPathPrefix } from "./hooks/webComponentPath";
import { injectGlobalStyles } from "./injectGlobalStyles";
import { prefetchEntryContent } from "./prefetchEntryContent";
import ProductShadowBase from "./productShadowBase";

injectGlobalStyles();

type EntryProps = Record<string, never>;

type EntryModule = {
  default: ComponentType<EntryProps>;
};

type EntryTagName =
  | "travel-entry"
  | "householder-entry"
  | "baby-entry"
  | "child-entry"
  | "dog-entry"
  | "cat-entry"
  | "van-entry"
  | "car-entry"
  | "mc-entry"
  | "landlord-entry"
  | "camper-entry";

const entryProductByTagName: Record<EntryTagName, ProductType> = {
  "travel-entry": ProductType.TRAVEL,
  "householder-entry": ProductType.HOUSEHOLDER,
  "baby-entry": ProductType.BABY,
  "child-entry": ProductType.CHILD,
  "dog-entry": ProductType.DOG,
  "cat-entry": ProductType.CAT,
  "van-entry": ProductType.VAN,
  "car-entry": ProductType.CAR,
  "mc-entry": ProductType.MC,
  "landlord-entry": ProductType.LANDLORD,
  "camper-entry": ProductType.CAMPER,
};

function defineLazyEntryElement(
  tagName: EntryTagName,
  manifestEntrySource: string,
  importEntry: () => Promise<EntryModule>,
  importNewEntry?: () => Promise<EntryModule>,
) {
  class LazyEntryElement extends ProductShadowBase {
    private get enableNextJsBff() {
      return this.getAttribute("enable-nextjs-bff") === "true";
    }

    connectedCallback() {
      this.manifestEntrySource = manifestEntrySource;
      this.primeResourceLoading();
      const language = getWebComponentLanguage(this.language ?? undefined);
      const appPathPrefix =
        this.getAttribute("app-path-prefix") ?? getWebComponentAppPathPrefix();
      const product = entryProductByTagName[tagName];
      prefetchEntryContent({
        product,
        language,
        backendHost: this.backendHost ?? undefined,
        appPathPrefix,
      });
      const importModule =
        this.enableNextJsBff && importNewEntry ? importNewEntry : importEntry;

      void importModule().then(({ default: Entry }) => {
        this.loadAndRender(() => createElement(Entry));
      });
    }
  }

  customElements.define(tagName, LazyEntryElement);
}
defineLazyEntryElement(
  "travel-entry",
  "src/web_components/travel/Entry.tsx",
  () => import("./travel/Entry"),
  () => import("./travel/NewTravelEntryForm"),
);
defineLazyEntryElement(
  "householder-entry",
  "src/web_components/householder/Entry.tsx",
  () => import("./householder/Entry"),
  () => import("./householder/NewHouseholderEntryForm"),
);
defineLazyEntryElement(
  "baby-entry",
  "src/web_components/baby/Entry.tsx",
  () => import("./baby/Entry"),
  () => import("./baby/NewBabyEntryForm"),
);
defineLazyEntryElement(
  "child-entry",
  "src/web_components/child/Entry.tsx",
  () => import("./child/Entry"),
  () => import("./child/NewChildEntryForm"),
);
defineLazyEntryElement(
  "dog-entry",
  "src/web_components/dog/Entry.tsx",
  () => import("./dog/Entry"),
  () => import("./dog/NewDogEntryForm"),
);
defineLazyEntryElement(
  "cat-entry",
  "src/web_components/cat/Entry.tsx",
  () => import("./cat/Entry"),
  () => import("./cat/NewCatEntryForm"),
);
defineLazyEntryElement(
  "van-entry",
  "src/web_components/van/Entry.tsx",
  () => import("./van/Entry"),
  () => import("./van/NewVanEntryForm"),
);
defineLazyEntryElement(
  "car-entry",
  "src/web_components/car/Entry.tsx",
  () => import("./car/Entry"),
  () => import("./car/NewCarEntryForm"),
);
defineLazyEntryElement(
  "mc-entry",
  "src/web_components/mc/Entry.tsx",
  () => import("./mc/Entry"),
  () => import("./mc/NewMcEntryForm"),
);
defineLazyEntryElement(
  "landlord-entry",
  "src/web_components/landlord/Entry.tsx",
  () => import("./landlord/Entry"),
  () => import("./landlord/NewLandlordEntryForm"),
);
defineLazyEntryElement(
  "camper-entry",
  "src/web_components/camper/Entry.tsx",
  () => import("./camper/Entry"),
  () => import("./camper/NewCamperEntryForm"),
);
